import { createSelector, PayloadAction } from '@reduxjs/toolkit';
import createAppSlice from 'app/createAppSlice';
import { RootState } from 'app/store';
import { PROMISE_STATUS } from 'utils/constants';

type AxiosArg = {
  url: string;
  params?: unknown;
};

type Meta = {
  arg: AxiosArg;
};

type ActionPayload = PayloadAction<void, string, Meta>;

type EndpointResponse = {
  params: unknown;
  error: unknown;
  success: boolean;
  pending: boolean;
};

interface IApiState {
  [endpoint: string]: EndpointResponse;
}

const splitString = (str: string, char: string) => str.split(char)[0];

function getAPIRequestedURL(actionType: string, url: string) {
  const method = splitString(splitString(actionType, '/'), '_').toLowerCase();
  return `${method}:${url}`;
}

const initialState: IApiState = {};

const apiSlice = createAppSlice({
  name: 'api',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) => action.type.endsWith(PROMISE_STATUS.pending),
        (state, { meta, type }: ActionPayload) => {
          if (meta?.arg?.url) {
            const requestURL = getAPIRequestedURL(type, meta.arg.url);
            state[requestURL] = {
              params: meta.arg.params,
              error: undefined,
              success: false,
              pending: true,
            };
          }
        },
      )
      .addMatcher(
        (action) => action.type.endsWith(PROMISE_STATUS.fulfilled),
        (state, { type, meta }: ActionPayload) => {
          if (meta?.arg?.url) {
            const requestURL = getAPIRequestedURL(type, meta.arg.url);
            state[requestURL] = {
              params: meta.arg.params,
              error: undefined,
              success: true,
              pending: false,
            };
          }
        },
      )
      .addMatcher(
        (action) => action.type.endsWith(PROMISE_STATUS.rejected),
        (state, { meta, type, payload }: ActionPayload) => {
          if (meta?.arg?.url) {
            const requestURL = getAPIRequestedURL(type, meta.arg.url);
            state[requestURL] = {
              params: meta.arg.params,
              error: payload,
              success: false,
              pending: false,
            };
          }
        },
      );
  },
});

export default apiSlice.reducer;

// @selectors
export const getApi = (state: RootState) => state.api;

const getApiState = createSelector(
  getApi,
  (_: RootState, endpointName: string, method: string) =>
    `${method.toLowerCase()}:${endpointName}`,
  (state, endpoint) => state[endpoint],
);

export const getSuccess = createSelector(
  getApiState,
  (apiState) => apiState?.success ?? false,
);

export const getError = createSelector(
  getApiState,
  (apiState) => apiState?.error ?? '',
);

export const getPending = createSelector(
  getApiState,
  (apiState) => apiState?.pending ?? false,
);
